import { useMutation } from '@apollo/client';
import { useNotifications } from '@sixfold/common-ui';
import { Children, renderChildren } from '@sixfold/typed-render-props';
import React from 'react';

import {
  CreateCompanyPlatformMutation as CreateCompanyPlatformMutationResult,
  CreateCompanyPlatformMutationVariables,
  DeleteCompanyPlatformMutation as DeleteCompanyPlatformMutationResponse,
  DeleteCompanyPlatformMutationVariables,
  UpdateCompanyPlatformMutation as UpdateCompanyPlatformMutationResult,
  UpdateCompanyPlatformMutationVariables,
} from '../../lib/graphql';
import {
  createCompanyPlatformMutation,
  deleteCompanyPlatformMutation,
  updateCompanyPlatformMutation,
} from '../graphql';

type PlatformMutationBaseInput = {
  company_id: string;
  platform_id: string;
};
interface Props {
  children: Children<{
    createCompanyPlatform: (input: PlatformMutationBaseInput & { company_id_on_platform: string }) => Promise<void>;
    updateCompanyPlatform: (input: PlatformMutationBaseInput & { company_id_on_platform: string }) => Promise<void>;
    deleteCompanyPlatform: (input: PlatformMutationBaseInput) => Promise<void>;
  }>;
}

export const PlatformMutations: React.FC<Props> = (props) => {
  const notify = useNotifications();
  const [createCompanyPlatform] = useMutation<
    CreateCompanyPlatformMutationResult,
    CreateCompanyPlatformMutationVariables
  >(createCompanyPlatformMutation);
  const [deleteCompanyPlatform] = useMutation<
    DeleteCompanyPlatformMutationResponse,
    DeleteCompanyPlatformMutationVariables
  >(deleteCompanyPlatformMutation);
  const [updateCompanyPlatform] = useMutation<
    UpdateCompanyPlatformMutationResult,
    UpdateCompanyPlatformMutationVariables
  >(updateCompanyPlatformMutation);

  return renderChildren(props.children as React.ReactNode, {
    createCompanyPlatform: async (input: PlatformMutationBaseInput & { company_id_on_platform: string }) => {
      const { company_id, ...rest } = input;

      const response = await createCompanyPlatform({
        variables: {
          company_id,
          input: rest,
        },
      });
      notify.success({ title: 'Company ID on platform created' });
      return response;
    },
    updateCompanyPlatform: async (input: PlatformMutationBaseInput & { company_id_on_platform: string }) => {
      const { company_id, platform_id, company_id_on_platform } = input;

      const response = await updateCompanyPlatform({
        variables: {
          company_id,
          platform_id,
          input: {
            company_id_on_platform,
          },
        },
      });
      notify.success({ title: 'Company ID on platform updated' });
      return response;
    },
    deleteCompanyPlatform: async (input: PlatformMutationBaseInput) => {
      const { company_id, ...rest } = input;

      const response = await deleteCompanyPlatform({
        variables: {
          company_id,
          input: rest,
        },
      });
      notify.success({ title: 'Company ID on platform deleted' });
      return response;
    },
  });
};
